// Experience-specific testimonials. Each entry mirrors the experience key
// from `data.js` (e.g. "desert", "sunrise-camel-trek") and carries 2-3
// short editorial quotes used inside the Highlights column on every
// /experiences/:key page.
//
// Schema:
//   {
//     name:    string                                 // guest name
//     quote:   { en, fr, es }                         // trilingual quote
//     place?:  string                                 // origin city / country
//     when?:   string                                 // month + year of the visit
//   }

const TESTIMONIALS_BY_KEY = {
  // ────────────────────────────────────────────────────────────────────
  // FLAGSHIPS
  // ────────────────────────────────────────────────────────────────────
  desert: [
    {
      name: "Camille L.",
      place: "Paris, France",
      when: "March 2024",
      quote: {
        en: "We went up the highest dune at dawn and watched the desert wake up in silence. I have never felt that small or that peaceful in my life.",
        fr: "Nous sommes montés sur la plus haute dune à l'aube et nous avons regardé le désert s'éveiller en silence. Je ne m'étais jamais sentie aussi petite — ni aussi sereine.",
        es: "Subimos a la duna más alta al amanecer y vimos al desierto despertar en silencio. Nunca me había sentido tan pequeña — ni tan en paz.",
      },
    },
    {
      name: "Mateo & Lucía",
      place: "Buenos Aires, Argentina",
      when: "October 2023",
      quote: {
        en: "The Sahrawi astronomer with his green laser, pointing at constellations we'd never heard of — that was the moment the whole trip clicked.",
        fr: "L'astronome sahraoui, son laser vert, des constellations qu'on n'avait jamais entendues — c'est à cet instant que tout le voyage a pris son sens.",
        es: "El astrónomo sahraui con su láser verde, señalando constelaciones que nunca habíamos oído — ese fue el momento en que todo el viaje cobró sentido.",
      },
    },
  ],

  atlas: [
    {
      name: "Sophie R.",
      place: "Lyon, France",
      when: "May 2024",
      quote: {
        en: "Our muleteer walked us through cherry villages where nobody had a watch. We came back with our minds quieter than when we left.",
        fr: "Notre muletier nous a guidés dans des villages de cerisaies où personne n'avait de montre. Nous sommes rentrés l'esprit plus calme qu'au départ.",
        es: "Nuestro arriero nos guio por aldeas de cerezos donde nadie llevaba reloj. Volvimos con la mente más tranquila que al salir.",
      },
    },
    {
      name: "James M.",
      place: "Edinburgh, UK",
      when: "September 2023",
      quote: {
        en: "Lunch on a Berber rooftop, three glasses of mint tea, the Atlas behind us. Easily the slowest, best meal of the year.",
        fr: "Déjeuner sur un toit berbère, trois verres de thé à la menthe, l'Atlas derrière nous. De loin, le repas le plus lent et le plus beau de l'année.",
        es: "Almuerzo en una azotea bereber, tres vasos de té con menta, el Atlas a la espalda. Sin duda, la comida más lenta y bella del año.",
      },
    },
  ],

  kasbah: [
    {
      name: "Hélène D.",
      place: "Geneva, Switzerland",
      when: "April 2024",
      quote: {
        en: "Walking through Aït Ben Haddou with someone whose family lived there for five generations — it stopped being a film set and started being a home.",
        fr: "Marcher dans Aït Ben Haddou avec quelqu'un dont la famille y vit depuis cinq générations — ce n'était plus un décor de cinéma, c'était une maison.",
        es: "Recorrer Aït Ben Haddou con alguien cuya familia vive allí desde hace cinco generaciones — dejó de ser un decorado y se volvió un hogar.",
      },
    },
    {
      name: "Daniela P.",
      place: "Milan, Italy",
      when: "November 2023",
      quote: {
        en: "The light at 5 PM on the red earth walls of Telouet — I still have the photograph above my desk a year later.",
        fr: "La lumière à 17 h sur les murs de terre rouge de Telouet — un an après, j'ai encore la photo au-dessus de mon bureau.",
        es: "La luz a las 17:00 sobre los muros de tierra roja de Telouet — un año después, sigo teniendo esa foto sobre mi escritorio.",
      },
    },
  ],

  berber: [
    {
      name: "Anna K.",
      place: "Berlin, Germany",
      when: "February 2024",
      quote: {
        en: "Fatima taught me a weaving knot I now use every morning in my own studio in Berlin. A small gesture I'll keep forever.",
        fr: "Fatima m'a appris un nœud de tissage que j'utilise désormais chaque matin dans mon atelier à Berlin. Un petit geste que je garderai toujours.",
        es: "Fátima me enseñó un nudo de tejido que ahora uso cada mañana en mi taller de Berlín. Un pequeño gesto que conservaré para siempre.",
      },
    },
    {
      name: "Théo & Margaux",
      place: "Toulouse, France",
      when: "June 2024",
      quote: {
        en: "Three gnaoua musicians, a courtyard, the call to prayer in the distance — we forgot to take photos for the first time in months.",
        fr: "Trois musiciens gnaoua, une cour intérieure, l'appel à la prière au loin — on a oublié de prendre des photos pour la première fois depuis des mois.",
        es: "Tres músicos gnaoua, un patio interior, la llamada a la oración a lo lejos — olvidamos hacer fotos por primera vez en meses.",
      },
    },
  ],

  // ────────────────────────────────────────────────────────────────────
  // XALUCA ORIGINALS
  // ────────────────────────────────────────────────────────────────────
  "sunrise-camel-trek": [
    {
      name: "Isabel V.",
      place: "Madrid, Spain",
      when: "March 2024",
      quote: {
        en: "We rode out in the dark. When the sun finally came up over the dunes, my husband cried — and I was the one who suggested doing it again the next morning.",
        fr: "Nous sommes partis dans la nuit. Quand le soleil s'est levé sur les dunes, mon mari a pleuré — et c'est moi qui ai proposé de recommencer le lendemain.",
        es: "Salimos cuando aún era de noche. Cuando por fin salió el sol sobre las dunas, mi marido lloró — y fui yo quien propuso repetirlo a la mañana siguiente.",
      },
    },
    {
      name: "Thomas B.",
      place: "Brussels, Belgium",
      when: "October 2023",
      quote: {
        en: "The camels were patient, the guide barely spoke — and that silence was the gift. We saw the desert before the desert woke up.",
        fr: "Les dromadaires patients, le guide à peine bavard — et ce silence-là, c'était le cadeau. Nous avons vu le désert avant qu'il ne se réveille.",
        es: "Los dromedarios pacientes, el guía apenas hablaba — y ese silencio fue el regalo. Vimos el desierto antes de que el desierto despertara.",
      },
    },
  ],

  "sunset-camel-trek": [
    {
      name: "Aiko T.",
      place: "Tokyo, Japan",
      when: "April 2024",
      quote: {
        en: "Forty minutes of orange light I will never see anywhere else. The whole way back, nobody on the caravan spoke a word.",
        fr: "Quarante minutes de lumière orange que je ne reverrai nulle part ailleurs. Sur le chemin du retour, personne dans la caravane n'a dit un mot.",
        es: "Cuarenta minutos de luz naranja que no veré en ninguna otra parte. En el camino de vuelta, nadie en la caravana dijo una palabra.",
      },
    },
    {
      name: "Familia García",
      place: "Sevilla, Spain",
      when: "December 2023",
      quote: {
        en: "Our kids (8 and 11) talked about that ride for the entire flight home. The dunes turning pink — they still draw them.",
        fr: "Nos enfants (8 et 11 ans) ont parlé de cette balade pendant tout le vol retour. Les dunes qui rosissent — ils les dessinent encore.",
        es: "Nuestros hijos (8 y 11 años) hablaron de ese paseo durante todo el vuelo de vuelta. Las dunas tiñéndose de rosa — todavía las dibujan.",
      },
    },
  ],

  "arfoud-pizza-night": [
    {
      name: "Marc & Émilie",
      place: "Marseille, France",
      when: "May 2024",
      quote: {
        en: "After three days of tagine, this was such a fun curveball. The pizzas were excellent, the owner sat with us — it felt like dinner with friends.",
        fr: "Après trois jours de tagine, c'était un détour très drôle. Les pizzas étaient excellentes, le patron s'est assis à notre table — un dîner entre amis.",
        es: "Tras tres días de tagine, este desvío fue divertidísimo. Las pizzas, excelentes; el dueño se sentó con nosotros — una cena entre amigos.",
      },
    },
    {
      name: "Sarah W.",
      place: "London, UK",
      when: "January 2024",
      quote: {
        en: "Honest Arfoud, off the tourist map. Two pizzas, two beers, the warmest welcome — the Xaluca concierge knew exactly what we needed that night.",
        fr: "L'Arfoud authentique, hors des sentiers balisés. Deux pizzas, deux bières, l'accueil le plus chaleureux — la conciergerie Xaluca savait exactement de quoi on avait besoin ce soir-là.",
        es: "El Arfoud auténtico, lejos del mapa turístico. Dos pizzas, dos cervezas, la acogida más cálida — el conserje de Xaluca supo exactamente lo que necesitábamos esa noche.",
      },
    },
  ],

  "quad-erg-chebbi-2h": [
    {
      name: "Lukas K.",
      place: "Vienna, Austria",
      when: "March 2024",
      quote: {
        en: "I'd never touched a quad. Twenty minutes in I was drifting up dune crests like a kid. The guides made the difference — calm, patient, fun.",
        fr: "Je n'avais jamais touché un quad. Vingt minutes plus tard, je dérapais sur les crêtes de dunes comme un gamin. Les guides ont fait toute la différence — calmes, patients, drôles.",
        es: "Nunca había tocado un quad. A los veinte minutos derrapaba por las crestas de dunas como un crío. Los guías marcaron la diferencia — tranquilos, pacientes, divertidos.",
      },
    },
    {
      name: "Priya & Rohan",
      place: "Mumbai, India",
      when: "November 2023",
      quote: {
        en: "Two hours felt like five minutes. We're already trying to book the four-hour version for next time.",
        fr: "Deux heures, ça nous a paru cinq minutes. On essaie déjà de réserver la version de quatre heures pour la prochaine fois.",
        es: "Dos horas nos parecieron cinco minutos. Ya estamos intentando reservar la versión de cuatro horas para la próxima.",
      },
    },
  ],

  "quad-erg-chebbi-4h": [
    {
      name: "Jonas P.",
      place: "Copenhagen, Denmark",
      when: "April 2024",
      quote: {
        en: "By hour two we were on a fossilised seabed nobody else was riding on. By hour four we were drinking tea in a nomad tent. Best half-day of the trip.",
        fr: "À la deuxième heure, on roulait sur un fond marin fossilisé, personne d'autre dans le coin. À la quatrième, on buvait le thé sous une tente nomade. La meilleure demi-journée du voyage.",
        es: "A la segunda hora rodábamos por un lecho marino fosilizado donde no había nadie más. A la cuarta, tomábamos té en una tienda nómada. La mejor media jornada del viaje.",
      },
    },
    {
      name: "Olivia & Ben",
      place: "Sydney, Australia",
      when: "September 2023",
      quote: {
        en: "We came for the dunes, we stayed for the silence at the turn-back point. Four hours that we'll be remembering for years.",
        fr: "On est venus pour les dunes, on est restés pour le silence au point de retour. Quatre heures dont on se souviendra des années.",
        es: "Vinimos por las dunas, nos quedamos por el silencio del punto de regreso. Cuatro horas que recordaremos durante años.",
      },
    },
  ],
};

export const getExperienceTestimonials = (key) => TESTIMONIALS_BY_KEY[key] || [];
